import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import './style.css';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { useSnackbar } from 'notistack';
import { ID } from './config';
import { CONNECTION_REMOVE } from '../../redux/actions/board';

const CreateNode = () => {

    const dispatch = useDispatch(),
    { enqueueSnackbar } = useSnackbar(),
    labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    { locale, modal } = useSelector( state => state.app ),
    { connections, currentNode, nodes } = useSelector( state => state.board ),
    [ connection, setConnection ] = useState( '' ),
    [ options, setOptions ] = useState([]),
    open = Boolean( modal === ID ),
    disabled = Boolean( !connection );

    /**
     * @name cancel
     * @function
     * @description Hides the modal
     */
    const cancel = useCallback(() => {
        dispatch({ type: MODAL_TOGGLE });
        setConnection('');
        setOptions([]);
    }, [ dispatch, setConnection, setOptions ]);

    /**
     * @name createOptions
     * @function
     * @description Create list of options so it doesn't cause a thousand redraws
     */
    const createOptions = useCallback(() => {

        const ary = [];

        currentNode.connections.forEach(
            id => {

                const c = connections[ id ];

                for( let i = 0; i < c.length; ++i ){
                    if( c[ i ] === currentNode.id ) continue;
                    ary.push({ id, name: nodes[ c[ i ] ].name });
                }
            }
        );

        setOptions([ ...ary ]);

    }, [ currentNode, connections, nodes, setOptions ])

    /**
     * @name disconnect
     * @function
     * @description Commit to the disconnect
     */
    const disconnect = () => {
        dispatch({ type: CONNECTION_REMOVE, payload: connection });
        cancel();
    }

    /**
     * @name onSelect
     * @funciton
     * @description Selects the connection to break
     * @param {EventListenerObject} e 
     */
    const onSelect = e => {
        setConnection( e.target.value )
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // if there's no connections to break
    useEffect(() => {
        if( open && currentNode.connections.length === 0 ){
            enqueueSnackbar(
                labels.noMoreConnections,
                { variant: 'info' }
            );
            cancel();
        }
    }, [ cancel, enqueueSnackbar, open, currentNode, labels.noMoreConnections ]);

    // create options when current node changes
    useEffect(() => {
        if( currentNode.connections?.length > 0  ) createOptions();
    }, [ currentNode, createOptions, setOptions ]);

    return (
        <Dialog open={ open } onClose={ cancel }>
            <DialogTitle>
                { 
                    labels.formatString(
                        labels.title,
                        currentNode?.name || ''
                    )
                }
            </DialogTitle>
            <DialogContent>
                <FormControl fullWidth>
                    <TextField
                        label={ labels.targetDisconnect } 
                        select
                        onChange={ onSelect }
                        required
                        value={ connection || '' }>
                            {
                                options.map(
                                    ( option, index ) => (
                                        <MenuItem key={ 'option-' + index } value={ option.id }>{ option.name }</MenuItem>
                                    )
                                )
                            }
                    </TextField>
                </FormControl> 
            </DialogContent>
            <DialogActions>
                <Button aria-label={ labels.disconnect } color="error" disabled={ disabled } onClick={ disconnect } variant="contained" startIcon={ <LinkOffIcon /> }>
                    { labels.disconnect }
                </Button>
                <Button aria-label={ labels.cancel } variant="outlined" onClick={ cancel } startIcon={ <NotInterestedIcon /> }>
                    { labels.cancel }
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CreateNode;